<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import GeoJsonDropZone from '#nora-ui/map/components/controls/GeoJsonDropZone.vue';
import Map from '#nora-ui/map/Map.vue';
import MapControls from '#nora-ui/map/MapControls.vue';
import { useMapStore } from '#nora-ui/map/stores/map-store';

const mapStore = useMapStore();
const { isBootstrapped, mapInstances } = storeToRefs(mapStore);

const maps = computed(() => {
  if (!isBootstrapped.value) {
    return [];
  }

  return mapInstances.value.map(instance => mapStore.getMapByIdOrThrow(instance.id));
});

const grid = computed(() => {
  const length = maps.value.length;

  const cols = Math.ceil(Math.sqrt(length));
  const rows = Math.ceil(length / cols);

  return {
    'display': 'grid',
    'grid-template-columns': `repeat(${cols}, minmax(0, 1fr))`,
    'grid-template-rows': `repeat(${rows}, minmax(0, 1fr))`,
  };
});

const lastCellStyle = computed(() => {
  const length = maps.value.length;

  if (length === 0) {
    return { 'grid-column': '' };
  }

  const cols = Math.ceil(Math.sqrt(length));
  const remainder = length % cols;

  if (remainder === 0) {
    return { 'grid-column': '' };
  }

  return {
    'grid-column': `${remainder} / span ${1 + cols - remainder}`,
  };
});

function getLastCellStyle(mapIndex: number) {
  if (mapIndex !== maps.value.length - 1) {
    return {};
  }

  return lastCellStyle.value;
}
</script>

<template>
  <div class="w-full h-full">
    <GeoJsonDropZone>
      <div class="h-full">
        <div
          class="w-full h-full overflow-hidden relative bg-light-bg-primary dark:bg-dark-bg-primary"
          :style="grid"
        >
          <div
            v-for="(map, mapIndex) of maps"
            :key="map.ref.id"
            :data-map-id="map.ref.id"
            class="flex flex-col relative rounded-sm overflow-hidden m-0.5 border border-light-separator-no-transparency dark:border-primary-750"
            :style="getLastCellStyle(mapIndex)"
          >
            <MapControls :map-id="map.ref.id" />
            <Map :map-id="map.ref.id" />
          </div>
        </div>
      </div>
    </GeoJsonDropZone>
  </div>
</template>
